// search.js — MHAA Member Search
// Loads the pre-built MiniSearch index and wires up the search UI.

import MiniSearch from 'minisearch';
import '@fortawesome/fontawesome-free/js/all';

let miniSearch = null;

// ---- DOM refs ----
const input = document.getElementById('searchInput');
const clearBtn = document.getElementById('clearBtn');
const resultsEl = document.getElementById('results');
const countEl = document.getElementById('resultCount');
const spinnerEl = document.getElementById('spinner');
const emptyEl = document.getElementById('emptyState');
const typingEl = document.getElementById('typingSuggestion');

// ---- Typing Animation ----
const suggestions = ["Anand", "Kumar", "1234/2006", "Rajesh", "Sudhakar"];
let suggestionIndex = 0;
let charIndex = 0;
let isDeleting = false;
let typingDelay = 100;

function type() {
  const currentSuggestion = suggestions[suggestionIndex];
  const typingTextEl = typingEl.querySelector('.typing-text');
  if (!typingTextEl) return;

  if (isDeleting) {
    typingTextEl.textContent = currentSuggestion.substring(0, charIndex - 1);
    charIndex--;
    typingDelay = 50;
  } else {
    typingTextEl.textContent = currentSuggestion.substring(0, charIndex + 1);
    charIndex++;
    typingDelay = 150;
  }

  if (!isDeleting && charIndex === currentSuggestion.length) {
    isDeleting = true;
    typingDelay = 2000; // Pause at the end
  } else if (isDeleting && charIndex === 0) {
    isDeleting = false;
    suggestionIndex = (suggestionIndex + 1) % suggestions.length;
    typingDelay = 500;
  }

  setTimeout(type, typingDelay);
}

// Start typing animation once index is loaded
typingEl.addEventListener('click', () => {
  const query = typingEl.querySelector('.typing-text').textContent;
  input.value = query;
  clearBtn.classList.remove('d-none');
  doSearch(query);
  input.focus();
});

// ---- Load index on page ready ----
async function loadIndex() {
  try {
    spinnerEl.classList.remove('d-none');

    // Use dynamic import so Parcel handles the JS module loading
    const data = await import('../assets/search-index.js');

    // MiniSearch.loadJSON expects a JSON string
    miniSearch = MiniSearch.loadJSON(JSON.stringify(data.default), {
      fields: ['name', 'enrollment'],
      storeFields: ['sno', 'lf_no', 'name', 'enrollment', 'nil_list_upto', 'list'],
    });
    spinnerEl.classList.add('d-none');
    typingEl.classList.remove('d-none');
    input.removeAttribute('disabled');
    input.focus();
    type(); // Start typing animation
  } catch (err) {
    spinnerEl.classList.add('d-none');
    resultsEl.innerHTML = `<div class="alert alert-danger">Failed to load search index. Please refresh the page.</div>`;
    console.error(err);
  }
}

// ---- Search Session State ----
let currentHits = [];
let currentQuery = '';
let renderedCount = 0;
const CHUNK_SIZE = 50;
let observer = null;

// ---- Search logic ----
function doSearch(query) {
  if (!miniSearch) return;

  query = query.trim();
  currentQuery = query;

  if (query.length === 0) {
    renderEmpty();
    return;
  }

  // Decide search mode: if the query looks like an enrollment number (digits/slashes)
  const isEnrollment = /^\d[\d/]*$/.test(query);

  if (isEnrollment) {
    // Exact prefix search on enrollment field only
    currentHits = miniSearch.search(query, {
      fields: ['enrollment'],
      prefix: true,
      fuzzy: false,
      combineWith: 'AND',
    });
  } else {
    // Fuzzy + prefix search on name field. 
    // DO NOT use fuzzy match if the query is extremely short (1-2 chars) to save massive memory allocation.
    currentHits = miniSearch.search(query, {
      fields: ['name'],
      prefix: true,
      fuzzy: query.length > 2 ? 0.2 : false,
      combineWith: 'AND',
    });
  }

  // Reset rendering state
  renderedCount = 0;
  resultsEl.innerHTML = '';

  // Setup initial UI (count & empty state)
  setupResultsUI();

  // Render first chunk
  if (currentHits.length > 0) {
    appendNextChunk();
    setupObserver();
  }
}

// ---- Rendering ----
function renderEmpty() {
  resultsEl.innerHTML = '';
  countEl.textContent = '';
  emptyEl.classList.remove('d-none');
  currentHits = [];
  renderedCount = 0;
  if (observer) observer.disconnect();
}

function setupResultsUI() {
  emptyEl.classList.add('d-none');
  if (observer) observer.disconnect();

  if (currentHits.length === 0) {
    countEl.textContent = 'No results found';
    resultsEl.innerHTML = `
      <div class="no-results shadow-sm border-0 bg-white rounded-4 p-5 text-center">
        <i class="fas fa-search fa-3x mb-3 text-muted opacity-25"></i>
        <h4 class="fw-bold">No matches found</h4>
        <p class="text-muted mb-0">No members found for "<strong>${escHtml(currentQuery)}</strong>"</p>
        <small class="text-muted d-block mt-2">Try a partial name or check the enrollment number format (e.g. 1234/2006)</small>
      </div>`;
    return;
  }

  countEl.textContent = `${currentHits.length.toLocaleString()} result${currentHits.length === 1 ? '' : 's'}`;
}

function appendNextChunk() {
  if (renderedCount >= currentHits.length) return;

  const chunk = currentHits.slice(renderedCount, renderedCount + CHUNK_SIZE);
  const isMobile = window.innerWidth < 768;

  // We append elements instead of rewriting innerHTML so we don't destroy what the user is looking at.
  const tempContainer = document.createElement('div');

  if (isMobile) {
    tempContainer.innerHTML = chunk.map((h, i) => `
      <div class="result-card d-block mb-3" style="animation-delay:${(i % 10) * 30}ms; border-left-color: #801c77;">
        <div class="card-header border-0 pb-2">
          <div class="name fw-bold" style="color: #333;">${highlight(escHtml(h.name), currentQuery)}</div>
          <div class="enroll"><code style="color: #801c77; background: rgba(128, 28, 119, 0.1); padding: 2px 6px; border-radius: 4px;">${highlight(escHtml(h.enrollment), currentQuery)}</code></div>
        </div>
        <div class="detail-row d-flex py-1 border-bottom border-light">
          <div class="label text-muted small fw-bold" style="width: 120px;">S.No:</div>
          <div class="value small">${h.sno}</div>
        </div>
        <div class="detail-row d-flex py-1 border-bottom border-light">
          <div class="label text-muted small fw-bold" style="width: 120px;">New LF No:</div>
          <div class="value small">${h.lf_no}</div>
        </div>
        <div class="detail-row d-flex py-1 border-bottom border-light">
          <div class="label text-muted small fw-bold" style="width: 120px;">List:</div>
          <div class="value small"><span class="badge rounded-pill" style="background: ${h.list === 'A' ? '#e8f5e9' : '#e3f2fd'}; color: ${h.list === 'A' ? '#2e7d32' : '#1565c0'};">${escHtml(h.list || '—')}</span></div>
        </div>
        <div class="detail-row d-flex py-1 pt-2">
          <div class="label text-muted small fw-bold" style="width: 120px;">NIL List Upto Dec 2025:</div>
          <div class="value small">${escHtml(h.nil_list_upto || '—')}</div>
        </div>
      </div>`).join('');

    while (tempContainer.firstChild) {
      resultsEl.appendChild(tempContainer.firstChild);
    }
  } else {
    // Desktop table handling
    const rows = chunk.map((h, i) => `
        <tr class="result-row" style="animation-delay:${(i % 20) * 15}ms">
            <td class="sno-cell text-muted small">${h.sno}</td>
            <td class="lf-cell text-muted small">${h.lf_no}</td>
            <td class="name-cell fw-bold" style="color: #1a1a1a;">${highlight(escHtml(h.name), currentQuery)}</td>
            <td class="enroll-cell"><code style="color: #801c77; background: rgba(128, 28, 119, 0.1); padding: 2px 8px; border-radius: 4px;">${highlight(escHtml(h.enrollment), currentQuery)}</code></td>
            <td class="list-cell"><span class="badge rounded-pill" style="background: ${h.list === 'A' ? '#e8f5e9' : '#e3f2fd'}; color: ${h.list === 'A' ? '#2e7d32' : '#1565c0'}; font-weight: 700;">${escHtml(h.list || '—')}</span></td>
            <td class="nil-cell"><span class="text-muted small">${escHtml(h.nil_list_upto || '—')}</span></td>
        </tr>`).join('');

    let tbody = resultsEl.querySelector('tbody');
    if (!tbody) {
      // First chunk: Create the table structure
      resultsEl.innerHTML = `
            <div class="table-wrapper shadow-sm rounded-4 overflow-hidden bg-white">
                <table class="table table-hover mb-0 align-middle" style="font-family: 'Arima Madurai', sans-serif;">
                <thead>
                    <tr style="background: #801c77; color: #fff;">
                    <th class="ps-4" style="background: inherit; color: inherit; padding: 16px;">S.No</th>
                    <th style="background: inherit; color: inherit; padding: 16px;">New LF No</th>
                    <th style="background: inherit; color: inherit; padding: 16px;">Name</th>
                    <th style="background: inherit; color: inherit; padding: 16px;">Enrollment</th>
                    <th style="background: inherit; color: inherit; padding: 16px;">List</th>
                    <th class="pe-4" style="background: inherit; color: inherit; padding: 16px;">NIL List Upto Dec 2025</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
                </table>
            </div>`;
    } else {
      // Subsequent chunks: Append to existing tbody
      tempContainer.innerHTML = `<table><tbody>${rows}</tbody></table>`;
      const newTbody = tempContainer.querySelector('tbody');
      while (newTbody.firstChild) {
        tbody.appendChild(newTbody.firstChild);
      }
    }
  }

  renderedCount += chunk.length;
}

function setupObserver() {
  // Remove old sentinel if it exists
  const oldSentinel = document.getElementById('scroll-sentinel');
  if (oldSentinel) oldSentinel.remove();

  if (renderedCount >= currentHits.length) return; // Everything rendered

  // Create sentinel at the bottom of the results area
  const sentinel = document.createElement('div');
  sentinel.id = 'scroll-sentinel';
  sentinel.style.height = '40px';
  resultsEl.appendChild(sentinel);

  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      appendNextChunk();
      // Move sentinel to the new bottom
      resultsEl.appendChild(sentinel);

      // Disconnect if we're done
      if (renderedCount >= currentHits.length) {
        observer.disconnect();
        sentinel.remove();
      }
    }
  }, { rootMargin: '200px' }); // Trigger 200px before reaching the exact bottom

  observer.observe(sentinel);
}

function highlight(text, query) {
  if (!query) return text;
  // Simple word-by-word highlight
  const words = query.trim().split(/\s+/).filter(w => w.length > 1);
  if (words.length === 0) return text;
  const pattern = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  return text.replace(pattern, '<mark style="background: #ffe58a; color: inherit; border-radius: 2px; padding: 0 1px;">$1</mark>');
}

function escHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ---- Event listeners ----
let debounceTimer;
input.addEventListener('input', () => {
  const q = input.value;
  clearBtn.classList.toggle('d-none', q.length === 0);
  clearTimeout(debounceTimer);
  // Increased debounce to 250ms to prevent mid-syllable search thrashing
  debounceTimer = setTimeout(() => doSearch(q), 250);
});

clearBtn.addEventListener('click', () => {
  input.value = '';
  clearBtn.classList.add('d-none');
  renderEmpty();
  input.focus();
});

loadIndex();
